import { useLocation, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { useSEO } from "../hooks/useSEO";
import { Reveal, openSchedule } from "./Reveal";
import { blogList } from "../lib/blogUtils";
import { FAQAccordion, StillHaveQuestions } from "./FAQSection";
import { CAREERS_FAQS, BRANDS_FAQS, RESTAURANTS_FAQS, BRAND_FAQS, FAQ_INTRO } from "../lib/faqData";

// Pages whose trailing "Frequently asked questions" question lists get upgraded to a working accordion
const FAQ_MAP = {
  "/careers": CAREERS_FAQS,
  "/for-brands": BRANDS_FAQS,
  "/for-restaurants": RESTAURANTS_FAQS,
  ...BRAND_FAQS,
};

const ctx = require.context("../data/migrated", false, /\.json$/);
const LOGO_MARKERS = ["0a0OuYO6VrMjYZocP9UPP0o3oA", "TnF7R1xvIuRAQyomnCG1TbVdps", "qO2olKnSoaEubftNOPCGThTU6JA", "TDjIJqTlxJCAkVhsoBpbMZzYg"];
// Images from the old site's shared team/footer module — excluded from blog galleries to avoid identical photos on every article
const SHARED_MODULE_MARKERS = ["GlaWPbHTtBgGOAFYCDQvEYteGb0", "9ck9OHHCQ7RILHdvIGGfrENB4fA", "8orj7R0glbGxmTuwm1J3C7qqNY0", "7jtRkW2ogifNbFAwCtMgIywPHI"];
const DATE_RE = /\d{2}\/\d{2}\/\d{4}/;

const pathToFile = (p) => (p === "/" ? "home" : p.replace(/^\//, "").replace(/\//g, "__").replace(/\.$/, ""));

const normalizeHref = (href) => {
  if (href.startsWith("mailto:") || href.startsWith("tel:")) return href;
  if (href.startsWith("../")) return "/" + href.replace(/^(\.\.\/)+/, "");
  if (href.includes("skopekitchens.com")) {
    try { return new URL(href).pathname; } catch { return href; }
  }
  return href;
};

const isExternal = (href) => href.startsWith("http") && !href.includes("skopekitchens.com");

const cleanLabel = (t) => {
  const half = t.length / 2;
  if (Number.isInteger(half) && t.slice(0, half) === t.slice(half)) return t.slice(0, half);
  return t;
};

const LinkText = ({ text, links }) => {
  if (!links?.length) return text;
  let parts = [text];
  links.forEach((l) => {
    parts = parts.flatMap((p) => {
      if (typeof p !== "string") return [p];
      const idx = p.indexOf(l.text);
      if (idx === -1) return [p];
      return [p.slice(0, idx), l, p.slice(idx + l.text.length)];
    });
  });
  return parts.map((p, i) =>
    typeof p === "string" ? (
      p
    ) : (
      <a
        key={i}
        href={normalizeHref(p.href)}
        {...(isExternal(p.href) ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        className="text-[#0e8fd9] underline underline-offset-4 decoration-[#0e8fd9]/40 hover:decoration-[#0e8fd9] transition-colors"
      >
        {p.text}
      </a>
    )
  );
};

const loadPage = (path) => {
  try {
    return ctx(`./${pathToFile(path)}.json`);
  } catch {
    return null;
  }
};

const API = process.env.REACT_APP_BACKEND_URL;

// Map a CMS blog document into the shape this renderer uses
const cmsToPage = (doc) => ({
  type: "blog",
  url: `/blog/${doc.slug}`,
  title: doc.seo?.title || doc.title,
  metaDescription: doc.seo?.metaDescription || doc.excerpt || "",
  canonical: doc.seo?.canonical || `https://www.skopekitchens.com/blog/${doc.slug}`,
  h1: doc.title,
  blocks: doc.content || [],
  images: doc.images?.length ? doc.images : doc.featuredImage?.src ? [doc.featuredImage] : [],
  contentLinks: doc.contentLinks || [],
  publishedAt: doc.publishedAt,
});

const Block = ({ b }) => {
  const inner = <LinkText text={b.text} links={b.links} />;
  if (b.type === "h2")
    return <h2 className="font-display font-semibold text-2xl lg:text-3xl text-ink tracking-tight mt-14 mb-2">{inner}</h2>;
  if (b.type === "h3")
    return <h3 className="font-display font-semibold text-xl lg:text-2xl text-ink/90 tracking-tight mt-10 mb-2">{inner}</h3>;
  if (b.type === "h4")
    return <h4 className="font-display font-semibold text-lg text-ink/85 mt-8 mb-2">{inner}</h4>;
  if (b.type === "li")
    return (
      <div className="flex gap-3 py-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-skope mt-2.5 shrink-0" />
        <span className="text-ink/60 leading-relaxed">{inner}</span>
      </div>
    );
  return <p className="text-ink/60 leading-relaxed my-4 text-base lg:text-lg">{inner}</p>;
};

const MigratedPage = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const staticData = loadPage(pathname);
  const isBlogPath = pathname.startsWith("/blog/");
  const [cmsData, setCmsData] = useState(null);
  const [cmsMiss, setCmsMiss] = useState(false);

  // Blog articles are served from the persistent CMS; the bundled copy is only a fallback
  useEffect(() => {
    setCmsData(null);
    setCmsMiss(false);
    if (!isBlogPath) return;
    const slug = pathname.split("/").filter(Boolean).pop();
    fetch(`${API}/api/blog/posts/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((doc) => setCmsData(cmsToPage(doc)))
      .catch(() => setCmsMiss(true));
  }, [pathname, isBlogPath]);

  const data = isBlogPath ? cmsData || staticData : staticData;

  // 404 guard: if the URL has a registered 301 redirect (e.g. after a slug change), follow it
  useEffect(() => {
    if (data || staticData || !cmsMiss || !isBlogPath) return;
    fetch(`${API}/api/blog/redirect?path=${encodeURIComponent(pathname)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => navigate(d.newPath, { replace: true }))
      .catch(() => {});
  }, [data, staticData, cmsMiss, isBlogPath, pathname, navigate]);
  useSEO({
    title: data?.title || "Page Not Found | Skope Kitchens",
    description: data?.metaDescription || "The page you are looking for could not be found.",
    canonical: data?.canonical || `https://www.skopekitchens.com${pathname}`,
  });

  useEffect(() => {
    if (!data || data.type !== "blog") return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: (data.h1 || data.title || "").replace(/ \| Skope Kitchens$/, ""),
      description: data.metaDescription,
      url: data.canonical,
      author: { "@type": "Organization", name: "Skope Kitchens" },
      publisher: { "@type": "Organization", name: "Skope Kitchens" },
    });
    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, [data]);

  if (!data) {
    return (
      <div className="bg-white min-h-screen text-ink font-body">
        <Navbar />
        <main className="max-w-[900px] mx-auto px-6 pt-40 pb-32" data-testid="not-found-page">
          <div className="font-mono text-[11px] tracking-[0.35em] text-skope uppercase">404</div>
          <h1 className="mt-6 font-display font-bold text-4xl lg:text-6xl tracking-tight">This page has moved on.</h1>
          <p className="mt-6 text-ink/55 leading-relaxed max-w-lg">
            The page you're looking for isn't here. Explore the Skope Kitchens homepage or browse our blog instead.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/" className="flex items-center justify-center gap-3 bg-skope text-ink font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-ink hover:text-white transition-colors" data-testid="404-home-link">
              Go Home <ArrowRight size={14} />
            </Link>
            <Link to="/blog" className="flex items-center justify-center gap-3 border border-ink/25 text-ink font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:border-skope hover:text-skope transition-colors" data-testid="404-blog-link">
              Read The Blog
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const isBlog = data.type === "blog";
  const contentImages = data.images.filter((i) => !LOGO_MARKERS.some((m) => i.src.includes(m)));
  const heroImage = contentImages[0];
  const galleryPool = isBlog
    ? contentImages.filter((i) => !SHARED_MODULE_MARKERS.some((m) => i.src.includes(m)))
    : contentImages;
  const gallery = galleryPool.slice(1, 5);
  const related = isBlog
    ? (() => {
        const idx = blogList.findIndex((b) => b.url === pathname);
        return [1, 2, 3].map((n) => blogList[(idx + n) % blogList.length]).filter(Boolean);
      })()
    : [];
  const quickLinks = Object.values(
    (data.contentLinks || []).reduce((acc, l) => {
      const href = normalizeHref(l.href);
      let label = cleanLabel(l.text);
      // never show a bare "Order Now" label — point it at the actual platform
      if (/^order now$/i.test(label)) {
        label = href.includes("zomato") ? "Order on Zomato" : href.includes("swiggy") ? "Order on Swiggy" : "Order Online";
      }
      if (!acc[href] || label.length < acc[href].label.length) acc[href] = { href, label, external: isExternal(l.href) };
      return acc;
    }, {})
  );
  const dateBlock = data.blocks.find((b) => b.type === "p" && DATE_RE.test(b.text));
  const blocks = data.blocks.filter((b, idx) => {
    if (idx === 0 && b.type === "h1") return false;
    if (b === dateBlock) return false;
    const prev = data.blocks[idx - 1];
    // drop paragraph that verbatim duplicates the bullet before it (extraction artifact)
    if (b.type === "p" && prev?.type === "li" && prev.text.trim() === b.text.trim()) return false;
    return true;
  });
  // cut the raw trailing FAQ question list — replaced by the interactive accordion below
  const faqSet = FAQ_MAP[pathname] || null;
  let contentBlocks = blocks;
  if (faqSet) {
    const cutIdx = blocks.findIndex(
      (b) => /frequently asked questions/i.test(b.text) || /^still have questions/i.test(b.text) || faqSet.some((f) => f.q === b.text)
    );
    if (cutIdx !== -1) contentBlocks = blocks.slice(0, cutIdx);
  }
  const displayTitle = (data.h1 || data.title || "").replace(/ \| Skope Kitchens$/, "");

  return (
    <div className="bg-white min-h-screen text-ink font-body" data-testid="migrated-page">
      <Navbar />
      <main className="pb-24">
        <div className="bg-ink bg-blueprint text-white pt-32 lg:pt-40 pb-12 lg:pb-16">
          <div className="max-w-[860px] mx-auto px-6">
            <Reveal>
              <div className="flex items-center gap-3 font-mono text-[11px] tracking-[0.35em] text-skope uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-skope" />
                {isBlog ? "Skope Blog" : "Skope Kitchens"}
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-6 font-display font-bold text-3xl lg:text-5xl tracking-tight leading-[1.1] text-white" data-testid="page-h1">
                {displayTitle}
              </h1>
            </Reveal>
            {dateBlock && (
              <Reveal delay={0.14}>
                <div className="mt-5 font-mono text-xs text-white/40 tracking-[0.15em] uppercase" data-testid="article-date">{dateBlock.text}</div>
              </Reveal>
            )}
          </div>
        </div>
        <article className="max-w-[860px] mx-auto px-6">
          {heroImage && (
            <Reveal delay={0.2}>
              <div className="mt-10 border border-ink/10 relative">
                <img src={heroImage.src} alt={heroImage.alt || displayTitle} loading="lazy" className="w-full max-h-[460px] object-cover"
                  onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                <div className="absolute -left-px -top-px w-6 h-6 border-l-2 border-t-2 border-skope" />
              </div>
            </Reveal>
          )}
          <div className="mt-12" data-testid="page-content">
            {contentBlocks.map((b, i) => <Block key={i} b={b} />)}
          </div>

          {faqSet && (
            <div className="mt-16" data-testid="page-faq">
              <Reveal>
                <div className="font-mono text-[11px] tracking-[0.35em] text-skope uppercase">FAQ</div>
                <h2 className="mt-4 font-display font-bold text-2xl lg:text-3xl tracking-tight text-ink">
                  Frequently asked questions
                </h2>
                <p className="mt-3 text-ink/55 leading-relaxed max-w-2xl">{FAQ_INTRO}</p>
              </Reveal>
              <div className="mt-8">
                <FAQAccordion faqs={faqSet} idPrefix={`page-faq-${pathname.replace(/[^a-z0-9]+/g, "-")}`} />
              </div>
              <StillHaveQuestions source={pathname} />
            </div>
          )}

          {quickLinks.length > 0 && (
            <div className="mt-14 border border-ink/10 p-6 lg:p-8" data-testid="quick-links">
              <div className="font-mono text-[10px] tracking-[0.3em] text-ink/40 uppercase">Links & Ordering</div>
              <div className="mt-4 flex flex-wrap gap-3">
                {quickLinks.map((l) => (
                  <a
                    key={l.href + l.label}
                    href={l.href}
                    {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    className="border border-ink/15 px-4 py-2.5 font-mono text-[11px] tracking-[0.1em] uppercase text-ink/70 hover:border-skope hover:text-skope transition-colors"
                  >
                    {l.label}
                  </a>
                ))}
              </div>
            </div>
          )}

          {gallery.length > 0 && (
            <div className="mt-16">
              <div className="font-mono text-[10px] tracking-[0.3em] text-ink/40 uppercase mb-6">From the original page</div>
              <div className="grid sm:grid-cols-2 gap-4">
                {gallery.map((img, i) => (
                  <img key={i} src={img.src} alt={img.alt || `${displayTitle} — image ${i + 2}`} loading="lazy"
                    className="w-full h-56 object-cover border border-ink/10"
                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ))}
              </div>
            </div>
          )}

          {isBlog && related.length > 0 && (
            <div className="mt-20" data-testid="related-articles">
              <div className="font-mono text-[10px] tracking-[0.3em] text-ink/40 uppercase">Keep Reading</div>
              <div className="mt-6 grid sm:grid-cols-3 gap-4">
                {related.map((r) => (
                  <Link key={r.url} to={r.url} className="group border border-ink/10 bg-white hover:border-skope/50 transition-colors" data-testid={`related-${r.url.split("/").pop()}`}>
                    <div className="aspect-[16/9] overflow-hidden bg-[#f6f6f6]">
                      {r.image && (
                        <img src={r.image.src} alt={r.image.alt || r.title} loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      )}
                    </div>
                    <div className="p-4">
                      <span className="font-mono text-[9px] tracking-[0.2em] text-skope uppercase">{r.category}</span>
                      <h3 className="mt-2 font-display font-semibold text-sm text-ink group-hover:text-skope transition-colors leading-snug line-clamp-2">{r.title}</h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-20 border border-skope/30 bg-ink text-white p-8 lg:p-10" data-testid="migrated-cta">
            <div className="font-mono text-[10px] tracking-[0.3em] text-skope uppercase">Schedule a Call</div>
            <h2 className="mt-3 font-display font-semibold text-2xl text-white tracking-tight">Building a food brand?</h2>
            <p className="mt-3 text-white/55 leading-relaxed max-w-lg">
              Let's build the kitchen and operating system behind it.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-4">
              <button onClick={() => openSchedule()} className="flex items-center justify-center gap-3 bg-skope text-ink font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-white hover:text-ink transition-colors" data-testid="migrated-cta-contact">
                Schedule a Call <ArrowRight size={14} />
              </button>
              <Link to="/#calculator" className="flex items-center justify-center gap-3 border border-white/25 text-white font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:border-skope hover:text-skope transition-colors" data-testid="migrated-cta-calculator">
                Calculate Your Kitchen
              </Link>
              {isBlog && (
                <Link to="/blog" className="flex items-center justify-center gap-3 border border-white/25 text-white font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:border-skope hover:text-skope transition-colors" data-testid="migrated-cta-blog">
                  <ArrowLeft size={14} /> All Articles
                </Link>
              )}
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default MigratedPage;
